import { api } from './api';
import type {
  PresignedUrlResponse,
  ProcessImageInput,
  Media,
  UpdateMediaInput,
} from '../types/entities';

export const mediaService = {
  /**
   * Get a presigned URL for uploading an image to MinIO
   */
  async getPresignedUrl(fileName: string, contentType: string, size: number): Promise<PresignedUrlResponse> {
    const response = await api.post<PresignedUrlResponse>('/api/admin/upload/presigned', {
      filename: fileName,  // Backend expects 'filename' not 'fileName'
      mimeType: contentType,  // Backend expects 'mimeType' not 'contentType'
      size,  // Backend requires size for validation
    });
    return response.data;
  },

  /**
   * Upload a file directly to MinIO using presigned URL
   */
  async uploadToMinIO(presignedUrl: string, file: File): Promise<void> {
    await fetch(presignedUrl, {
      method: 'PUT',
      body: file,
      headers: {
        'Content-Type': file.type,
      },
    });
  },

  /**
   * Trigger image processing after upload
   */
  async processImage(input: ProcessImageInput): Promise<Media> {
    const response = await api.post<Media>('/api/admin/upload/process', input);
    return response.data;
  },

  /**
   * Complete upload workflow: presigned URL → upload → process
   * @param file - The file to upload
   * @param options - Optional title (SEO filename) and alt text
   */
  async uploadImage(file: File, options?: { title?: string; alt?: string }): Promise<Media> {
    // Step 1: Get presigned URL
    const { uploadUrl, tempKey } = await this.getPresignedUrl(file.name, file.type, file.size);

    // Step 2: Upload to MinIO
    await this.uploadToMinIO(uploadUrl, file);

    // Step 3: Process image (WebP conversion, thumbnails)
    const media = await this.processImage({
      tempKey,
      title: options?.title,
      alt: options?.alt,
    });

    // Return the full media object
    return media;
  },

  /**
   * Update media metadata (title, alt)
   */
  async updateMedia(id: string, data: UpdateMediaInput): Promise<Media> {
    const response = await api.patch<Media>(`/api/admin/upload/media/${id}`, data);
    return response.data;
  },

  /**
   * Get all media files with pagination
   */
  async getAllMedia(page = 1, limit = 20) {
    const response = await api.get('/api/admin/upload/media', {
      params: { page, limit },
    });
    return response.data;
  },

  /**
   * Delete a media file
   */
  async deleteMedia(id: string): Promise<void> {
    await api.delete(`/api/admin/upload/media/${id}`);
  },
};
